/**
 * InvoiceHeader — Header gradient do checkout público.
 *
 * RELEASE: 2026-06-27 v2.8.0 — Gradiente enriquecido (3 stops + overlay radial
 *          de luz no canto superior direito) e marca d'água sutil com logo
 *          Uaizy no canto inferior direito para acabamento premium.
 * RELEASE: 2026-06-22 v2.4.0 — Alinha blocos do recebedor/pagador ao topo.
 * RELEASE: 2026-06-21 v2.0.0 — Logo centralizada removida em versão posterior.
 * RELEASE: 2026-06-21 v1.0.0 — Versão inicial.
 */

import type { CSSProperties } from 'react';
import uaizyLogo from '@/assets/logo-uaizy-icon.png';

interface SellerBranding {
  tenant_name: string;
  logo_url: string | null;
  document?: { type: 'cpf' | 'cnpj' | null; number: string | null } | null;
  contact_email?: string | null;
  phone?: string | null;
  address?: {
    cep?: string | null;
    street?: string | null;
    number?: string | null;
    city?: string | null;
    state?: string | null;
  } | null;
}

interface PayerInfo {
  display_name?: string | null;
  documento?: string | null;
  tipo?: 'pf' | 'pj' | null;
}

interface InvoiceHeaderProps {
  seller: SellerBranding;
  payer: PayerInfo | null | undefined;
  /** Gradient (#aaaaaa → #bbbbbb) já sanitizado. */
  gradient: { from: string; to: string };
}

/**
 * Formata telefone brasileiro em (DD) 9XXXX-XXXX / (DD) XXXX-XXXX.
 * Remove DDI 55 inicial quando presente. Fallback: retorna string original.
 */
function formatPhoneBR(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  const local = digits.length >= 12 && digits.startsWith('55') ? digits.slice(2) : digits;
  if (local.length === 11) return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return raw;
}

export function InvoiceHeader({ seller, payer, gradient }: InvoiceHeaderProps) {
  // Gradiente enriquecido: 3 stops das cores da marca + overlay radial branco
  // sutil no canto superior direito (efeito "glow" premium). Cores ainda vêm
  // sanitizadas (#RRGGBB) via safeBrandColor.
  const bgStyle: CSSProperties = {
    backgroundImage: [
      // Luz radial suave (acabamento premium)
      'radial-gradient(120% 80% at 100% 0%, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 55%)',
      // Sombra radial sutil no canto inferior esquerdo para profundidade
      'radial-gradient(90% 60% at 0% 100%, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0) 60%)',
      // Gradiente principal da marca (3 stops para transição mais rica)
      `linear-gradient(135deg, ${gradient.from} 0%, ${gradient.to} 55%, ${gradient.from} 100%)`,
    ].join(', '),
    color: 'var(--brand-foreground)',
  };

  const addr = seller.address;
  const addrLine1 = [addr?.street, addr?.number].filter(Boolean).join(', ');
  const cityUf = [addr?.city, addr?.state].filter(Boolean).join('/');
  const cep = addr?.cep ? `CEP: ${addr.cep}` : '';
  const addrLine2 = [cityUf, cep].filter(Boolean).join(' – ');

  // Classes compartilhadas para linhas secundárias (mesma escala nos dois lados).
  // v2.4.0: peso reduzido (font-normal) para que o nome ganhe destaque.
  const secondaryLine = 'text-sm md:text-base font-normal opacity-90 leading-snug';

  return (
    <header className="px-4 md:px-9 pt-6 md:pt-10 pb-10 md:pb-10 relative overflow-hidden" style={bgStyle}>
      {/* Marca d'água Uaizy — decorativa, oculta no mobile para não cobrir texto. */}
      <img
        src={uaizyLogo}
        alt=""
        aria-hidden
        className="pointer-events-none select-none absolute -bottom-6 -right-6 w-40 md:w-56 opacity-10 hidden md:block"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 items-start gap-4 md:gap-6">
        {/* Bloco do emissor (recebedor) — alinhado à esquerda em todos os breakpoints */}
        <div className="space-y-2 max-w-md text-left">
          <h1 className="text-2xl md:text-[32px] font-extrabold tracking-tight leading-tight">
            {seller.tenant_name}
          </h1>
          <div className={`${secondaryLine} space-y-1`}>
            {seller.document?.number && (
              <p>
                {seller.document.type === 'cnpj' ? 'CNPJ' : 'CPF'}:{' '}
                {seller.document.number}
              </p>
            )}
            {seller.contact_email && <p className="break-all">{seller.contact_email}</p>}
            {seller.phone && <p>{formatPhoneBR(seller.phone)}</p>}
            {addrLine1 && <p>{addrLine1}</p>}
            {addrLine2 && <p>{addrLine2}</p>}
          </div>
        </div>

        {/* Bloco do pagador — esquerda no mobile, direita no desktop */}
        {payer?.display_name ? (
          <div className="text-left md:text-right space-y-1 max-w-md">
            <p className="text-2xl md:text-[32px] font-extrabold tracking-tight uppercase leading-tight">
              {payer.display_name}
            </p>
            {payer.documento && (
              <p className={secondaryLine}>
                {payer.tipo === 'pj' ? 'CNPJ' : 'CPF'}: {payer.documento}
              </p>
            )}
          </div>
        ) : (
          <div aria-hidden />
        )}
      </div>

    </header>
  );
}
